// Pickups, delivery runs and driver cash (03 §3.4 and §3.6, 04 §4.5, 06 §6.3). Pure: which
// shipments can go out on a run and why the rest cannot, the suggested order of the stops, the
// run and stop state machines, the loading and scan-back reconciliation, what a driver holds,
// and how a delivery is verified. The box moves themselves stay in box.ts.
import { isFinalStatus, activeHolds, type BoxStatus, type Hold } from './box-types';
import { formatMoney, isCurrencyCode, money } from './money';
import type { Permission } from './permissions';

// ------------------------------------------------------------------- geography

export interface GeoPoint {
  readonly lat: number;
  readonly lng: number;
}

const EARTH_RADIUS_KM = 6371.0088;
const radians = (degrees: number): number => (degrees * Math.PI) / 180;

/** Great-circle distance in kilometres (haversine). */
export function distanceKm(a: GeoPoint, b: GeoPoint): number {
  const dLat = radians(b.lat - a.lat);
  const dLng = radians(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(radians(a.lat)) * Math.cos(radians(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

export interface OrderableStop {
  readonly id: string;
  readonly geo?: GeoPoint | undefined;
}

/**
 * The suggested order of a run: nearest neighbour, starting from the branch. Stops with no
 * position cannot be placed, so they come last in the order they were given. Without a starting
 * point the first stop with a position starts the route. Ties keep the order given.
 */
export function suggestStopOrder(
  origin: GeoPoint | undefined,
  stops: readonly OrderableStop[],
): string[] {
  const placed = stops.filter((s) => s.geo !== undefined);
  const unplaced = stops.filter((s) => s.geo === undefined);
  const order: string[] = [];
  const remaining = [...placed];
  let here = origin;
  while (remaining.length > 0) {
    let best = 0;
    if (here !== undefined) {
      const from = here;
      let bestKm = Number.POSITIVE_INFINITY;
      remaining.forEach((stop, index) => {
        const km = distanceKm(from, stop.geo as GeoPoint);
        if (km < bestKm) {
          bestKm = km;
          best = index;
        }
      });
    }
    const [next] = remaining.splice(best, 1);
    order.push((next as OrderableStop).id);
    here = (next as OrderableStop).geo;
  }
  return [...order, ...unplaced.map((s) => s.id)];
}

/** Length of a route in kilometres, branch first, for the planner's summary. */
export function routeKm(origin: GeoPoint | undefined, points: readonly GeoPoint[]): number {
  let total = 0;
  let here = origin;
  for (const point of points) {
    if (here !== undefined) total += distanceKm(here, point);
    here = point;
  }
  return total;
}

// ------------------------------------------------------- what can go on a delivery run

export const BLOCK_CODES = [
  'not_own_delivery',
  'wrong_branch',
  'wrong_zone',
  'unpaid',
  'customs_hold',
  'on_hold',
  'already_planned',
  'nothing_left',
  'not_at_branch',
] as const;
export type BlockCode = (typeof BLOCK_CODES)[number];

export interface Blocker {
  readonly code: BlockCode;
  readonly message: string;
}

export interface RunScope {
  /** The branch the run starts from. */
  readonly branchId: string;
  /** The zones the run covers. Empty means any zone the branch serves. */
  readonly zoneIds: readonly string[];
}

export interface DeliveryBox {
  readonly barcode: string;
  readonly status: BoxStatus;
  readonly currentBranchId: string;
  readonly holds: readonly Hold[];
}

export interface DeliveryShipment {
  readonly trackingNo: string;
  readonly currency?: string | undefined;
  readonly mode?: string | undefined;
  readonly servingBranchId?: string | undefined;
  readonly zoneId?: string | undefined;
  /** Still owed on the invoice, destination charges included. */
  readonly balanceMinor: number;
  /** The part of the balance that is a destination charge to collect at the door. */
  readonly destinationDueMinor?: number | undefined;
  /** The open run it is already planned on, if any. */
  readonly plannedRunId?: string | undefined;
  readonly boxes: readonly DeliveryBox[];
}

export interface DeliveryAssessment {
  readonly trackingNo: string;
  readonly ok: boolean;
  /** The boxes that can go on this run. */
  readonly barcodes: readonly string[];
  /** Live boxes of the shipment that cannot go yet (still travelling), with `ok` still true. */
  readonly heldBack: readonly string[];
  readonly blockers: readonly Blocker[];
}

function amountText(minor: number, currency: string | undefined): string {
  return currency !== undefined && isCurrencyCode(currency)
    ? formatMoney(money(minor, currency))
    : `${minor} ${currency ?? ''}`.trim();
}

/** Why a shipment cannot go on the run, or the boxes that can (04 §4.5 `planRun`). */
export function assessForDelivery(
  shipment: DeliveryShipment,
  run: RunScope,
  options: { readonly ignoreRunId?: string } = {},
): DeliveryAssessment {
  const blockers: Blocker[] = [];
  if (shipment.mode !== 'own') {
    blockers.push({
      code: 'not_own_delivery',
      message: 'This city is served by a partner, not by our own drivers.',
    });
  } else if (shipment.servingBranchId !== run.branchId) {
    blockers.push({
      code: 'wrong_branch',
      message: `This shipment is delivered from ${shipment.servingBranchId ?? 'another branch'}, not ${run.branchId}.`,
    });
  }
  if (
    run.zoneIds.length > 0 &&
    (shipment.zoneId === undefined || !run.zoneIds.includes(shipment.zoneId))
  ) {
    blockers.push({
      code: 'wrong_zone',
      message: `It is not in the zones of this run (${run.zoneIds.join(', ')}).`,
    });
  }
  const dueAtDoor = Math.min(shipment.destinationDueMinor ?? 0, Math.max(0, shipment.balanceMinor));
  const unpaidMinor = shipment.balanceMinor - dueAtDoor;
  if (unpaidMinor > 0) {
    blockers.push({
      code: 'unpaid',
      message: `Balance due: ${amountText(unpaidMinor, shipment.currency)}. Collect it at the counter first.`,
    });
  }
  const live = shipment.boxes.filter((box) => !isFinalStatus(box.status));
  if (live.length === 0) {
    blockers.push({ code: 'nothing_left', message: 'Every box is already delivered or finished.' });
  }
  const held = live.flatMap((box) => activeHolds(box));
  const customs = held.filter((hold) => hold.type === 'customs');
  const others = held.filter((hold) => hold.type !== 'customs');
  if (customs.length > 0) {
    blockers.push({
      code: 'customs_hold',
      message: 'Customs have not released it (customs hold).',
    });
  }
  if (others.length > 0) {
    const types = [...new Set(others.map((hold) => hold.type.replace(/_/g, ' ')))];
    blockers.push({ code: 'on_hold', message: `On hold: ${types.join(', ')}.` });
  }
  if (shipment.plannedRunId !== undefined && shipment.plannedRunId !== options.ignoreRunId) {
    blockers.push({
      code: 'already_planned',
      message: `Already on run ${shipment.plannedRunId}.`,
    });
  }
  const eligible = live.filter(
    (box) =>
      box.status === 'at_branch' &&
      box.currentBranchId === run.branchId &&
      activeHolds(box).length === 0,
  );
  if (eligible.length === 0 && live.length > 0 && held.length === 0) {
    blockers.push({
      code: 'not_at_branch',
      message: `No box is at ${run.branchId} ready to go out (${live
        .map((box) => `${box.barcode}: ${box.status.replace(/_/g, ' ')}`)
        .join(', ')}).`,
    });
  }
  const heldBack = live
    .filter((box) => !eligible.some((e) => e.barcode === box.barcode))
    .map((box) => box.barcode);
  return {
    trackingNo: shipment.trackingNo,
    ok: blockers.length === 0,
    barcodes: blockers.length === 0 ? eligible.map((box) => box.barcode) : [],
    heldBack: blockers.length === 0 ? heldBack : [],
    blockers,
  };
}

export interface PlanCandidates {
  readonly ready: readonly DeliveryAssessment[];
  readonly blocked: readonly DeliveryAssessment[];
}

/** Splits a list of shipments into the ones that can go out on the run and the ones that cannot. */
export function planCandidates(
  shipments: readonly DeliveryShipment[],
  run: RunScope,
  options: { readonly ignoreRunId?: string } = {},
): PlanCandidates {
  const all = shipments.map((s) => assessForDelivery(s, run, options));
  return { ready: all.filter((a) => a.ok), blocked: all.filter((a) => !a.ok) };
}

/**
 * Destination charges still to collect: what the invoice carries as destination charges, less what
 * was collected, never more than the balance (06 §6.3).
 */
export function destinationChargeDue(input: {
  readonly chargesMinor: number;
  readonly collectedMinor: number;
  readonly balanceMinor: number;
}): number {
  return Math.max(
    0,
    Math.min(input.chargesMinor - input.collectedMinor, Math.max(0, input.balanceMinor)),
  );
}

// ------------------------------------------------------------ run and stop machines

export const RUN_STATUS_LIST = ['planned', 'assigned', 'active', 'closed'] as const;
export type RunState = (typeof RUN_STATUS_LIST)[number];
export const RUN_ACTIONS = ['assign', 'edit', 'start', 'work', 'close'] as const;
export type RunAction = (typeof RUN_ACTIONS)[number];

interface RunRow {
  readonly from: RunState;
  readonly action: RunAction;
  readonly to: RunState;
}

/**
 * `planned → assigned → active → closed`. Stops can be changed until the run closes. Reloading a
 * van that has already left (`start` while active) just scans more boxes. `work` (arrive, deliver,
 * fail, complete) needs an active run. An unstarted run cannot be closed by a driver.
 */
export const RUN_TRANSITIONS: readonly RunRow[] = [
  { from: 'planned', action: 'assign', to: 'assigned' },
  { from: 'assigned', action: 'assign', to: 'assigned' },
  { from: 'planned', action: 'edit', to: 'planned' },
  { from: 'assigned', action: 'edit', to: 'assigned' },
  { from: 'active', action: 'edit', to: 'active' },
  { from: 'assigned', action: 'start', to: 'active' },
  { from: 'active', action: 'start', to: 'active' },
  { from: 'active', action: 'work', to: 'active' },
  { from: 'active', action: 'close', to: 'closed' },
];

export function runTransition(from: RunState, action: RunAction): RunState | undefined {
  return RUN_TRANSITIONS.find((row) => row.from === from && row.action === action)?.to;
}

export function legalRunActions(from: RunState): RunAction[] {
  return [...new Set(RUN_TRANSITIONS.filter((row) => row.from === from).map((r) => r.action))];
}

// ------------------------------------------------------------- who may work a run

/** Asks whether the person holds a permission (a super admin holds them all). */
export type HoldsPermission = (permission: Permission) => boolean;

/** A stop as far as permissions go: its kind and whether it still counts (not skipped). */
export interface RunStopKind {
  readonly kind: 'delivery' | 'pickup';
  readonly status?: StopState;
}

/** A run carries deliveries when a delivery stop on it has not been skipped. */
export function runHasDeliveries(stops: readonly RunStopKind[]): boolean {
  return stops.some((s) => s.kind === 'delivery' && s.status !== 'skipped');
}

export type StartRunBlock =
  /** Neither `finalmile.deliver` nor `pickup.complete`. */
  | 'no_permission'
  /** The run carries deliveries and the person holds only `pickup.complete`. */
  | 'delivery_permission';

/**
 * May this driver start their run (`startRun`, 04 §4.5)? Both `finalmile.deliver` and
 * `pickup.complete` open the command, but a run that carries deliveries needs `finalmile.deliver`:
 * the load scan sends its boxes out for delivery, and a driver who holds only `pickup.complete` could
 * then deliver none of them (`deliverStop` and `failStop` need `finalmile.deliver`). A pickup-only
 * run starts with either. Null when the person may start it.
 */
export function startRunBlock(
  holds: HoldsPermission,
  stops: readonly RunStopKind[],
): StartRunBlock | null {
  if (holds('finalmile.deliver')) return null;
  if (!holds('pickup.complete')) return 'no_permission';
  return runHasDeliveries(stops) ? 'delivery_permission' : null;
}

/** What the office may record on someone's run (Runs today), exactly what the server accepts. */
export interface OfficeRunActions {
  /**
   * `startRun` for the driver (the loading scan, by the office when the driver's phone died or on a
   * test run): `finalmile.deliver` and `finalmile.plan`, like a delivery for the driver.
   */
  readonly start: boolean;
  /** `deliverStop` with `onBehalfOfUid`: the command's `finalmile.deliver` and `finalmile.plan`. */
  readonly deliver: boolean;
  /** `failStop`: `finalmile.deliver`, and the run's own driver or `finalmile.plan`. */
  readonly fail: boolean;
  /** `closeRun`: `finalmile.plan`, or the run's own driver with `finalmile.deliver` or `pickup.complete`. */
  readonly close: boolean;
}

export function officeRunActions(
  holds: HoldsPermission,
  who: { uid?: string | null | undefined },
  run: { driverUid?: string | undefined },
): OfficeRunActions {
  const plan = holds('finalmile.plan');
  const deliver = holds('finalmile.deliver');
  const own =
    run.driverUid !== undefined && typeof who.uid === 'string' && run.driverUid === who.uid;
  return {
    start: deliver && plan && run.driverUid !== undefined,
    deliver: deliver && plan && run.driverUid !== undefined,
    fail: deliver && (plan || own),
    close: plan || (own && (deliver || holds('pickup.complete'))),
  };
}

export const STOP_STATUS_LIST = ['pending', 'arrived', 'done', 'failed', 'skipped'] as const;
export type StopState = (typeof STOP_STATUS_LIST)[number];
export const STOP_ACTIONS = ['arrive', 'complete', 'fail', 'skip'] as const;
export type StopAction = (typeof STOP_ACTIONS)[number];

const OPEN_STOP: readonly StopState[] = ['pending', 'arrived', 'failed'];

/**
 * A stop is open until it is done or skipped. A failed stop can be tried again the same day
 * (`failed → arrived/done/failed`); the number of failures is counted on the stop.
 */
export function stopTransition(from: StopState, action: StopAction): StopState | undefined {
  if (!OPEN_STOP.includes(from)) return undefined;
  switch (action) {
    case 'arrive':
      return 'arrived';
    case 'complete':
      return 'done';
    case 'fail':
      return 'failed';
    case 'skip':
      return 'skipped';
  }
}

export function isStopOpen(status: StopState): boolean {
  return OPEN_STOP.includes(status);
}

/** Stops that never got a result when the run closes. */
export function stopsToSkip(statuses: readonly StopState[]): number {
  return statuses.filter((s) => s === 'pending' || s === 'arrived').length;
}

// ----------------------------------------------------------- loading and scan-back

export interface LoadingCheck {
  readonly loaded: readonly string[];
  /** Planned for the run but not scanned: they stay at the branch (a warning, not a refusal). */
  readonly notLoaded: readonly string[];
  /** Scanned but not on any stop of the run. */
  readonly strangers: readonly string[];
}

/** What a loading scan covered of what the run planned (04 §4.5 `startRun`). */
export function checkLoading(
  planned: readonly string[],
  scanned: readonly string[],
  alreadyLoaded: readonly string[] = [],
): LoadingCheck {
  const plannedSet = new Set(planned);
  const done = new Set(alreadyLoaded);
  const scannedSet = new Set(scanned);
  const loaded = [...scannedSet].filter((code) => plannedSet.has(code));
  const strangers = [...scannedSet].filter((code) => !plannedSet.has(code));
  const notLoaded = planned.filter((code) => !scannedSet.has(code) && !done.has(code));
  return { loaded, notLoaded, strangers };
}

export interface RunReconciliation {
  /** Went out and was delivered. */
  readonly delivered: readonly string[];
  /** Went out and came back to the branch. */
  readonly back: readonly string[];
  /** Went out, was not delivered and was not scanned back. */
  readonly missing: readonly string[];
  /** Scanned back although it was never loaded on this run. */
  readonly unexpectedBack: readonly string[];
}

/** Close-run reconciliation (03 §3.6, 04 §4.5 `closeRun`). Every list is in first-seen order. */
export function reconcileRun(input: {
  readonly loaded: readonly string[];
  readonly delivered: readonly string[];
  readonly scannedBack: readonly string[];
}): RunReconciliation {
  const loaded = new Set(input.loaded);
  const delivered = new Set(input.delivered);
  const scannedBack = [...new Set(input.scannedBack)];
  const deliveredOut = [...loaded].filter((code) => delivered.has(code));
  const back = scannedBack.filter((code) => loaded.has(code) && !delivered.has(code));
  const backSet = new Set(back);
  return {
    delivered: deliveredOut,
    back,
    missing: [...loaded].filter((code) => !delivered.has(code) && !backSet.has(code)),
    unexpectedBack: scannedBack.filter((code) => !loaded.has(code)),
  };
}

// ----------------------------------------------------------------- driver cash

export interface Custody {
  readonly heldMinor: number;
  readonly heldSince?: string | undefined;
}

/**
 * The driver's custody after a collection (positive) or a handover. `heldSince` marks when the
 * balance last rose above zero: the cash-overnight clock counts from it.
 */
export function custodyAfter(before: Custody, deltaMinor: number, at: string): Custody {
  if (!Number.isSafeInteger(deltaMinor))
    throw new Error('A cash movement is a whole number of minor units');
  const heldMinor = before.heldMinor + deltaMinor;
  if (heldMinor <= 0) return { heldMinor };
  return {
    heldMinor,
    heldSince: before.heldMinor > 0 && before.heldSince !== undefined ? before.heldSince : at,
  };
}

export type HandoverOutcome = 'exact' | 'short' | 'over';

export interface HandoverResult {
  readonly heldMinor: number;
  readonly countedMinor: number;
  /** Counted minus held. Negative is short. */
  readonly varianceMinor: number;
  readonly outcome: HandoverOutcome;
}

/** The cashier counts what the driver hands over against what the books say the driver holds. */
export function reconcileHandover(heldMinor: number, countedMinor: number): HandoverResult {
  if (!Number.isSafeInteger(heldMinor) || heldMinor < 0)
    throw new Error('Held cash is a whole, non-negative amount');
  if (!Number.isSafeInteger(countedMinor) || countedMinor < 0)
    throw new Error('Counted cash is a whole, non-negative amount');
  const varianceMinor = countedMinor - heldMinor;
  return {
    heldMinor,
    countedMinor,
    varianceMinor,
    outcome: varianceMinor === 0 ? 'exact' : varianceMinor < 0 ? 'short' : 'over',
  };
}

/** What a door collection leaves unsettled, and whether it is more than the consignee owes. */
export function judgeCollection(input: {
  readonly dueMinor: number;
  readonly collectedMinor: number;
}): {
  readonly ok: boolean;
  readonly shortMinor: number;
} {
  return {
    ok: input.collectedMinor <= input.dueMinor,
    shortMinor: Math.max(0, input.dueMinor - input.collectedMinor),
  };
}

// -------------------------------------------------------------------- verification

export type Verification = 'code' | 'id_photo';

export type VerificationVerdict =
  | { readonly ok: true; readonly method: Verification; readonly codeMismatch: boolean }
  | { readonly ok: false; readonly reason: 'verification_required' | 'wrong_code' };

/**
 * How the consignee proved who they are (04 §4.5 `deliverStop`): the four-digit code sent to
 * them, or a photo of their ID when there is no code. A wrong code is accepted only with an ID
 * photo, and then the office is told. `codeMatches` is asked only when a code was given and issued.
 */
export function judgeVerification(input: {
  readonly code: string | undefined;
  readonly codeIssued: boolean;
  readonly idPhotoPath: string | undefined;
  readonly codeMatches: (code: string) => boolean;
}): VerificationVerdict {
  const hasPhoto = input.idPhotoPath !== undefined && input.idPhotoPath !== '';
  const usable = input.code !== undefined && /^\d{4}$/.test(input.code) && input.codeIssued;
  if (usable && input.codeMatches(input.code as string)) {
    return { ok: true, method: 'code', codeMismatch: false };
  }
  if (hasPhoto) return { ok: true, method: 'id_photo', codeMismatch: usable };
  return { ok: false, reason: usable ? 'wrong_code' : 'verification_required' };
}

// ------------------------------------------------------------------ pickup requests

export const PICKUP_STATE_LIST = [
  'requested',
  'scheduled',
  'assigned',
  'en_route',
  'completed',
  'failed',
  'cancelled',
] as const;
export type PickupState = (typeof PICKUP_STATE_LIST)[number];
export const PICKUP_ACTIONS = [
  'schedule',
  'assign',
  'unassign',
  'start',
  'complete',
  'fail',
  'cancel',
] as const;
export type PickupAction = (typeof PICKUP_ACTIONS)[number];

interface PickupRow {
  readonly from: PickupState;
  readonly action: PickupAction;
  readonly to: PickupState;
}

/**
 * `requested → scheduled → assigned → en_route → completed / failed`; `cancelled` until the van is
 * on its way. A failed request can be scheduled again (03 §3.4).
 */
export const PICKUP_TRANSITIONS: readonly PickupRow[] = [
  { from: 'requested', action: 'schedule', to: 'scheduled' },
  { from: 'scheduled', action: 'schedule', to: 'scheduled' },
  { from: 'failed', action: 'schedule', to: 'scheduled' },
  { from: 'requested', action: 'assign', to: 'assigned' },
  { from: 'scheduled', action: 'assign', to: 'assigned' },
  { from: 'failed', action: 'assign', to: 'assigned' },
  { from: 'assigned', action: 'unassign', to: 'scheduled' },
  { from: 'en_route', action: 'unassign', to: 'scheduled' },
  { from: 'assigned', action: 'start', to: 'en_route' },
  { from: 'assigned', action: 'complete', to: 'completed' },
  { from: 'en_route', action: 'complete', to: 'completed' },
  { from: 'assigned', action: 'fail', to: 'failed' },
  { from: 'en_route', action: 'fail', to: 'failed' },
  { from: 'requested', action: 'cancel', to: 'cancelled' },
  { from: 'scheduled', action: 'cancel', to: 'cancelled' },
  { from: 'assigned', action: 'cancel', to: 'cancelled' },
  { from: 'failed', action: 'cancel', to: 'cancelled' },
];

export function pickupTransition(from: PickupState, action: PickupAction): PickupState | undefined {
  return PICKUP_TRANSITIONS.find((row) => row.from === from && row.action === action)?.to;
}

/** Pickup requests the dispatcher still has to schedule: the 24-hour clock runs on these. */
export function awaitsScheduling(status: PickupState): boolean {
  return status === 'requested' || status === 'failed';
}
